import { LOCALE } from "./format";

/**
 * Hourly jobs: priced by the hour, not as a flat quote. The backend sends
 * `job.hourly` (null for a normal job) with the movers, rate and booked hours
 * from Zoho / Paperless; the price is worked out here. There is no minimum
 * and no admin fee (removed per Miles, 2026-10-15), and the deposit is not
 * shown yet.
 */

export const isHourlyJob = (job) =>
  !!job && job.moveType !== "ld" && !!job.hourly && typeof job.hourly === "object";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** "$215", "$1,342.50" — whole dollars stay whole, cents show when there are some. */
export const money = (n) =>
  "$" +
  num(n).toLocaleString(LOCALE, {
    minimumFractionDigits: Number.isInteger(num(n)) ? 0 : 2,
    maximumFractionDigits: 2,
  });

/**
 * The hourly numbers for a job, or null when it is not an hourly job. The
 * estimate bills exactly the booked hours; the final price does the same with
 * the hours worked on the updated BOL. No hours booked = no estimate.
 */
export const hourlyCalc = (job) => {
  if (!isHourlyJob(job)) return null;
  const h = job.hourly;
  const movers = num(h.movers);
  const rate = num(h.rate);
  const booked = num(h.bookedHours);
  const bol = num(h.bolHours);
  const bolTotal = num(h.bolTotal);
  // The updated contract's own total wins when the BOL carries one; otherwise hours worked × rate.
  const final = bolTotal > 0 ? bolTotal : rate * bol;
  const est = rate * booked;

  return {
    movers,
    rate,
    booked,
    estHours: booked,
    est,
    bol,
    finalHours: bol,
    final,
    // Post Move: the updated contract is in once the BOL has hours or a total.
    hasFinal: bol > 0 || bolTotal > 0,
    diff: final - est,
    // An hourly job with no hourly contract in Paperless yet (the card flags it).
    contractOnFile: h.contractOnFile !== false,
  };
};

/** True when an hourly job still has no hourly contract in Paperless. */
export const hourlyContractMissing = (job) => isHourlyJob(job) && job.hourly.contractOnFile === false;
